import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnimal } from '../api';
import { useParams, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getSettings } from '../api';
import {
  ArrowLeft, Calendar, Dna, Droplets, Heart, ChevronLeft, ChevronRight, X, Cat, Palette
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AnimalProfilePage = ({ user, onLogout }) => {
  const { id } = useParams();
  const [animal, setAnimal] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    Promise.all([
      getAnimal(id),
      getSettings()
    ]).then(([animalRes, settingsRes]) => {
      setAnimal(animalRes.data);
      setSettings(settingsRes.data);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EEF3F8]">
        <div className="w-12 h-12 border-4 border-[#C5D0DA] border-t-[#7A9BB5] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!animal) {
    return (
      <div className="min-h-screen bg-[#EEF3F8] flex items-center justify-center">
        <div className="text-center">
          <Cat size={48} className="text-[#C5D0DA] mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-[#2E3A46] mb-2">Tier nicht gefunden</h2>
          <Link to="/" className="text-[#7A9BB5] hover:text-[#2E3A46]">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  const photos = animal.photos || [];

  return (
    <div className="min-h-screen bg-[#EEF3F8]">
      <Navigation user={user} onLogout={onLogout} settings={settings} isLanding={false} />

      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-8 md:py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#7A9BB5] hover:text-[#2E3A46] text-sm mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          Zurück
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Photos */}
          <div>
            {photos.length > 0 ? (
              <div className="space-y-3">
                <div
                  className="aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() => setLightbox({ open: true, index: 0 })}
                >
                  <img
                    src={`${BACKEND_URL}${photos[0]}`}
                    alt={animal.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                {photos.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {photos.slice(1, 5).map((photo, idx) => (
                      <div
                        key={idx}
                        className="aspect-square rounded-xl overflow-hidden cursor-pointer"
                        onClick={() => setLightbox({ open: true, index: idx + 1 })}
                      >
                        <img
                          src={`${BACKEND_URL}${photo}`}
                          alt={`${animal.name} ${idx + 2}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-[4/3] rounded-2xl bg-white border-2 border-dashed border-[#C5D0DA] flex items-center justify-center">
                <Cat size={64} className="text-[#C5D0DA]" />
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="mb-2">
              {animal.prefix && (
                <p className="text-xs text-[#7A9BB5] uppercase tracking-wider mb-1">{animal.prefix}</p>
              )}
              <h1 className="font-heading text-3xl sm:text-4xl text-[#2E3A46] mb-3">{animal.name}</h1>
              {animal.base_color && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D8E8F0] text-[#2E3A46] text-sm">
                  <Palette size={14} className="text-[#7A9BB5]" />
                  {animal.base_color}
                </span>
              )}
            </div>

            {animal.character_text && (
              <div className="mt-6">
                <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Wesen & Charakter</h3>
                <p className="text-[#354551] leading-relaxed">{animal.character_text}</p>
              </div>
            )}

            {animal.bio && (
              <div className="mt-4">
                <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Biographie</h3>
                <p className="text-[#354551] leading-relaxed">{animal.bio}</p>
              </div>
            )}

            {/* Details Grid */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              {animal.born_at && (
                <div className="card-frost p-4">
                  <div className="flex items-center gap-2 text-xs text-[#7A9BB5] mb-1">
                    <Calendar size={14} /> Geburtsdatum
                  </div>
                  <p className="text-sm text-[#2E3A46] font-medium">{animal.born_at}</p>
                </div>
              )}
              {animal.blood_group && (
                <div className="card-frost p-4">
                  <div className="flex items-center gap-2 text-xs text-[#7A9BB5] mb-1">
                    <Droplets size={14} /> Blutgruppe
                  </div>
                  <p className="text-sm text-[#2E3A46] font-medium">{animal.blood_group}</p>
                </div>
              )}
              {animal.dilution_gene && (
                <div className="card-frost p-4">
                  <div className="flex items-center gap-2 text-xs text-[#7A9BB5] mb-1">
                    <Dna size={14} /> Verdünnung
                  </div>
                  <p className="text-sm text-[#2E3A46] font-medium">{animal.dilution_gene}</p>
                </div>
              )}
              {animal.felidae_number && (
                <div className="card-frost p-4">
                  <div className="flex items-center gap-2 text-xs text-[#7A9BB5] mb-1">
                    <Heart size={14} /> Felidae Nr.
                  </div>
                  <p className="text-sm text-[#2E3A46] font-medium">{animal.felidae_number}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && photos.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setLightbox({ open: false, index: 0 })}>
          <button className="absolute top-4 right-4 text-white z-10 p-2" onClick={() => setLightbox({ open: false, index: 0 })}>
            <X size={28} />
          </button>
          {photos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white z-10 p-2"
                onClick={(e) => { e.stopPropagation(); setLightbox(p => ({ ...p, index: (p.index - 1 + photos.length) % photos.length })); }}
              >
                <ChevronLeft size={32} />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white z-10 p-2"
                onClick={(e) => { e.stopPropagation(); setLightbox(p => ({ ...p, index: (p.index + 1) % photos.length })); }}
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}
          <img
            src={`${BACKEND_URL}${photos[lightbox.index]}`}
            alt={animal.name}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer settings={settings} />
    </div>
  );
};

export default AnimalProfilePage;
